/**
 * iOS / Safari render + virtualization consistency auditor.
 *
 * Enable with session debug:
 *   localStorage.setItem('__ULTRA_SESSION_DEBUG', '1'); location.reload();
 *
 * Console:
 *   __ultraSession.auditDom()
 *   __ultraSession.renderStats()
 */

const counters = {
   viewportMount: 0,
   viewportUnmount: 0,
   viewportRemount: 0,
   contentRefresh: 0,
   resync: 0,
   scrollHostBump: 0,
   dragStart: 0,
   dragEnd: 0,
   fastScrollEnter: 0,
   fastScrollExit: 0,
   inconsistentSnapshots: 0,
};

let lastViewportAlive = 0;
let logFn = null;

function isEnabled() {
   if (typeof window === "undefined") return false;
   if (window.__ULTRA_SESSION_DEBUG === true) return true;
   try {
      return localStorage.getItem("__ULTRA_SESSION_DEBUG") === "1";
   } catch {
      return false;
   }
}

export function setUltraRenderAuditLogger(fn) {
   logFn = typeof fn === "function" ? fn : null;
}

function emit(category, event, data = null) {
   if (!isEnabled()) return;
   if (logFn) {
      logFn(category, event, data);
      return;
   }
   if (typeof console !== "undefined" && console.debug) {
      console.debug(`[ULTRA:${category}] ${event}`, data ?? "");
   }
}

export function ultraRenderMark(event, data = null) {
   if (!isEnabled()) return;
   if (Object.prototype.hasOwnProperty.call(counters, event)) {
      counters[event] += 1;
   }
   emit("RENDER", event, data);
}

export function ultraRenderMarkViewportMount() {
   if (!isEnabled()) return;
   counters.viewportMount += 1;
   if (lastViewportAlive > 0) {
      counters.viewportRemount += 1;
      emit("WARN", "viewport-overlap-mount", {
         alreadyAlive: lastViewportAlive,
      });
   }
   lastViewportAlive += 1;
   emit("RENDER", "viewport-mount", { alive: lastViewportAlive });
}

export function ultraRenderMarkViewportUnmount() {
   if (!isEnabled()) return;
   counters.viewportUnmount += 1;
   lastViewportAlive = Math.max(0, lastViewportAlive - 1);
   emit("RENDER", "viewport-unmount", { alive: lastViewportAlive });
}

/**
 * Layout identity for a pooled cell. Prefer translate3d (what UltraCell sets);
 * fall back to getBoundingClientRect for nodes without an inline transform.
 * @returns {{ x: number, y: number } | null}
 */
function readPoolCellLayout(el) {
   if (!(el instanceof Element)) return null;
   const transform = String(el.style?.transform || "");
   const match = transform.match(
      /translate3d\(\s*(-?[\d.]+)px\s*,\s*(-?[\d.]+)px/i,
   );
   if (match) {
      return {
         x: Math.round(Number(match[1])),
         y: Math.round(Number(match[2])),
      };
   }
   const rect = el.getBoundingClientRect?.();
   if (!rect || rect.width < 1 || rect.height < 1) return null;
   return {
      x: Math.round(rect.left),
      y: Math.round(rect.top),
   };
}

/**
 * Live DOM consistency check — call when a visual glitch is observed.
 */
export function auditUltraGridDom(root = null) {
   const doc = typeof document !== "undefined" ? document : null;
   if (!doc) {
      return { ok: false, reason: "no-document" };
   }

   const scope =
      root instanceof Element
         ? root
         : doc.querySelector(".ultra-view") || doc.body;

   const viewports = scope.querySelectorAll(".ultra-structure-grid");
   const scrollHosts = scope.querySelectorAll(".ultra-track-pan");
   const stickyHeaders = scope.querySelectorAll(".ultra-day-sticky");
   const selectionBoxes = scope.querySelectorAll(
      ".ultra-structure-grid__selection-box",
   );
   // Pool cells only — exclude reservation overlay cards that intentionally
   // sit on top of the matching pooled slot (same x/y by design).
   const poolCells = scope.querySelectorAll(
      ".ultra-structure-grid__cell:not(.is-overlay-reservation)",
   );
   const hourLabelCells = scope.querySelectorAll(
      '.ultra-structure-grid__cell:not(.is-overlay-reservation)[data-cell-type="hour"], .ultra-structure-grid__cell:not(.is-overlay-reservation)[data-cell-type="lateral-hour"]',
   );
   const lateralCells = scope.querySelectorAll(
      ".ultra-structure-grid__cell.is-lateral",
   );
   const allCells = scope.querySelectorAll(".ultra-structure-grid__cell");
   const canvases = scope.querySelectorAll(".ultra-structure-grid__canvas");
   const reservationLayers = scope.querySelectorAll(
      ".ultra-structure-grid__reservation-layer",
   );

   // Real ghosts: two pool cells at the same layout (x,y). Prefer the
   // translate3d written by UltraCell (source of truth); fall back to rect.
   // Same slot label on one row across instructor columns is NORMAL.
   const positionOwners = new Map();
   const duplicateHourPairs = [];
   poolCells.forEach((el) => {
      const layout = readPoolCellLayout(el);
      if (!layout) return;
      const posKey = `${layout.x},${layout.y}`;
      const meta = {
         x: layout.x,
         y: layout.y,
         label: String(el.dataset?.slotLabel || "")
            .trim()
            .slice(0, 16),
         cellType: String(el.dataset?.cellType || ""),
         slotKey: String(el.dataset?.ultraSlotKey || "").trim(),
         instId: String(el.dataset?.instId || "").trim(),
      };
      if (positionOwners.has(posKey)) {
         const other = positionOwners.get(posKey);
         duplicateHourPairs.push({
            label: meta.label || other.label || "?",
            x: layout.x,
            y: layout.y,
            a: other,
            b: meta,
         });
      } else {
         positionOwners.set(posKey, meta);
      }
   });

   // Same logical slot key mounted twice in the pool (overlay excluded).
   const slotKeyCounts = new Map();
   const duplicateSlotKeys = [];
   poolCells.forEach((el) => {
      const slotKey = String(el.dataset?.ultraSlotKey || "").trim();
      if (!slotKey) return;
      const next = (slotKeyCounts.get(slotKey) || 0) + 1;
      slotKeyCounts.set(slotKey, next);
      if (next === 2) {
         duplicateSlotKeys.push({ slotKey, count: next });
      } else if (next > 2) {
         const entry = duplicateSlotKeys.find((d) => d.slotKey === slotKey);
         if (entry) entry.count = next;
      }
   });

   let cellsWithTranslate3d = 0;
   allCells.forEach((el) => {
      const t = el.style?.transform || "";
      if (t.includes("translate3d")) cellsWithTranslate3d += 1;
   });

   const snapshot = {
      ts: new Date().toISOString(),
      grids: viewports.length,
      scrollHosts: scrollHosts.length,
      stickyHeaders: stickyHeaders.length,
      selectionBoxes: selectionBoxes.length,
      hourCells: hourLabelCells.length,
      poolCells: poolCells.length,
      lateralCells: lateralCells.length,
      allCells: allCells.length,
      canvases: canvases.length,
      reservationLayers: reservationLayers.length,
      cellsWithTranslate3d,
      duplicateHourPairs,
      duplicateSlotKeys,
      counters: { ...counters },
      viewportAliveTracked: lastViewportAlive,
   };

   const problems = [];
   if (snapshot.grids > 1) problems.push(`grids=${snapshot.grids}`);
   if (snapshot.scrollHosts > 1) {
      problems.push(`scrollHosts=${snapshot.scrollHosts}`);
   }
   if (snapshot.selectionBoxes > snapshot.grids) {
      problems.push(`selectionBoxes=${snapshot.selectionBoxes}`);
   }
   if (duplicateHourPairs.length) {
      problems.push(`overlappingCells=${duplicateHourPairs.length}`);
   }
   if (duplicateSlotKeys.length) {
      problems.push(`duplicateSlotKeys=${duplicateSlotKeys.length}`);
   }
   if (lastViewportAlive > 1) {
      problems.push(`viewportAlive=${lastViewportAlive}`);
   }

   snapshot.ok = problems.length === 0;
   snapshot.problems = problems;

   if (!snapshot.ok) {
      counters.inconsistentSnapshots += 1;
      emit("WARN", "dom-inconsistent", snapshot);
   } else {
      emit("RENDER", "dom-audit-ok", snapshot);
   }

   return snapshot;
}

export function getUltraRenderStats() {
   return {
      counters: { ...counters },
      viewportAliveTracked: lastViewportAlive,
   };
}

export function resetUltraRenderStats() {
   Object.keys(counters).forEach((key) => {
      counters[key] = 0;
   });
   lastViewportAlive = 0;
}

export function installUltraRenderAuditApi(apiTarget) {
   if (!apiTarget || typeof apiTarget !== "object") return;
   apiTarget.auditDom = (root) => auditUltraGridDom(root);
   apiTarget.renderStats = () => getUltraRenderStats();
   apiTarget.resetRenderStats = () => resetUltraRenderStats();
}
